import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { PrimaryButton } from '../../../../../components/Buttons/PrimaryButton';
import { FormUnsavedChangesPrompt } from '../../../../../components/FormUnsavedChangesPrompt';
import { HelpButtonContainer } from '../../../../../components/HelpButton/container.tsx';
import { PageHeading } from '../../../../../components/PageHeading';
import { VM_START_HELP_LINKS } from '../../../../../constants/user-guide';
import { startStopOrderSelectors } from '../../../../../redux/configuration/start-stop-order/slice';
import {
  useGetVmStartOrderItemQuery,
  useGetVmStartOrderQuery,
  useUpdateVmStartOrderMutation,
} from '../../../../../redux/configuration/start-stop-order/vm-start/api';
import { useLeaveFormPrompt } from '../../../hooks/useLeaveFormPrompt';
import { StartStopOrderContent } from '../components/StartStopOrderContent';
import { VMStartTable } from './components/VMStartTable';

export function VMStart(): ReactElement {
  const { t } = useTranslation();
  const { uid: topologyUid } = useParams<EditTopologyParams>();
  const { data, isFetching, isError, refetch, isLoading } =
    useGetVmStartOrderQuery(topologyUid);
  const {
    isFetching: isFetchingItem,
    isError: isErrorFetchingItem,
    isLoading: isLoadingItem,
  } = useGetVmStartOrderItemQuery(data?.uid || '', {
    skip: data?.uid === undefined,
  });
  const [updateVMStartOrder, { isLoading: isUpdating }] =
    useUpdateVmStartOrderMutation();
  const isDirty = useSelector(startStopOrderSelectors.getStartStopOrderIsDirty);
  const startStopOrder = useSelector(startStopOrderSelectors.getStartStopOrder);

  const { showModal, handleConfirm, handleCancel } =
    useLeaveFormPrompt(isDirty);

  return (
    <section className="half-padding-left">
      <PageHeading
        ctaBtn={
          <PrimaryButton
            className="pull-right dbl-margin-top"
            testId="vm-start-order-submit"
            type="submit"
            colour="success"
            onClick={() => updateVMStartOrder(startStopOrder!)}
            disabled={!isDirty}
            loading={isUpdating}
          >
            {t('buttons.update')}
          </PrimaryButton>
        }
        pageTitle={t('startStopOrder.vmStart.name')}
      />
      {!isError && !isErrorFetchingItem && startStopOrder && (
        <StartStopOrderContent
          isFetching={isFetching || isFetchingItem}
          isLoading={isLoading || isLoadingItem}
          buttonUnorderedLabel={t('buttons.concurrent')}
          buttonOrderedLabel={t('buttons.sequential')}
          ordered={startStopOrder.ordered}
          positions={startStopOrder.positions}
          alertText={t('startStopOrder.vmStart.table.noResults')}
          table={VMStartTable}
          refetch={refetch}
        />
      )}
      <FormUnsavedChangesPrompt
        showModal={showModal}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />
      <HelpButtonContainer links={VM_START_HELP_LINKS} />
    </section>
  );
}
